import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft, faRotate, faClockRotateLeft, faCheckCircle, faClock, faLock,
  faTimesCircle, faBan, faHourglassHalf, faStream, faPlusCircle, faHandshake, faMoneyBillTransfer,
  faUnlock, faCoins, faFlagCheckered, faCircleDot, faSearch, faTimes, faFileExport,
  faChevronDown, faChevronUp, faUpload,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { UiPageHeader, UiButton, UiModal, UiInput, UiFilterPanel, StatusBadge } from '../../components/ui';
import type { StatusDef } from '../../components/ui';
import DataTable from '../../components/shared/DataTable';
import type { Column } from '../../components/shared/DataTable';
import CountdownTimer from '../../components/shared/CountdownTimer';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import { LiveViewToggle, LiveModeBufferBadge } from '../../components/shared/LiveViewToggle';
import socketService from '../../services/socketService';
import { generateScbFileAll, downloadTextFile } from '../../utils/scbExport';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';
const today = () => dayjs().tz(TZ).format('YYYY-MM-DD');

interface AdminP2pPayout {
  id: string;
  payout_number: string;
  amount: number;
  allocated_amount?: number;
  funded_amount?: number;
  status: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  window_expires_at?: string;
  created_at: string;
  slip_url?: string | null; // สลิปหลักฐานเว็บโอนส่วนที่เหลือ (ตอนปิดงาน force-close)
  merchant?: { id: string; name: string; email: string; p2p_fee_payout_pct?: number | null; payout_fee_percentage?: number | null };
  // ข้อมูล export ล่าสุด (เฉพาะ status=pending_transfer)
  transfer_response?: { batch_ref?: string; filename?: string; exported_at?: string; exported_by_name?: string; prev_status?: string } | null;
}

// ค่าธรรมเนียมถอน P2P = amount × pct (p2p_fee_payout_pct fallback payout_fee_percentage)
const payoutFeeOf = (p: AdminP2pPayout) => {
  const pct = p.merchant?.p2p_fee_payout_pct != null ? Number(p.merchant.p2p_fee_payout_pct) : Number(p.merchant?.payout_fee_percentage || 0);
  return { pct, fee: Math.round(Number(p.amount) * pct) / 100 };
};

interface PairRow {
  id: string;
  order_number: string;
  amount: number;
  status: string;
  approval_method?: string | null;
  created_at: string;
  paid_at?: string;
  merchant?: { name: string };
}

// ช่องทางที่ทำให้สำเร็จ (auto จากสลิป vs แอดมิน)
const approvalInfo = (m?: string | null) => {
  if (m === 'manual_admin') return { label: 'แอดมินอนุมัติ', cls: 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400' };
  if (m === 'manual_admin_skip') return { label: 'แอดมิน (ข้ามตรวจ)', cls: 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400' };
  return { label: 'อัตโนมัติ (สลิป)', cls: 'bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400' };
};

// จัดกลุ่มประวัติเพื่อสรุป
const HIST_GROUPS: { key: string; label: string; cls: string }[] = [
  { key: 'paid', label: 'สำเร็จ', cls: 'text-green-600 dark:text-green-400' },
  { key: 'pending', label: 'รอโอน', cls: 'text-amber-600 dark:text-amber-400' },
  { key: 'expired_locked', label: 'หมดอายุ (ล็อก)', cls: 'text-amber-600 dark:text-amber-400' },
  { key: 'expired', label: 'หมดอายุ', cls: 'text-gray-500 dark:text-slate-400' },
  { key: 'cancelled', label: 'ยกเลิก', cls: 'text-gray-500 dark:text-slate-400' },
];

const STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faHourglassHalf, label: 'รอจับคู่' },
  p2p_completed: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  p2p_partial: { bg: 'bg-indigo-100 dark:bg-indigo-500/15', text: 'text-indigo-700 dark:text-indigo-400', icon: faClock, label: 'รอเว็บโอน' },
  pending_transfer: { bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-700 dark:text-blue-400', icon: faFileExport, label: 'Export แล้ว รอยืนยันโอน' },
  expired: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faTimesCircle, label: 'หมดอายุ' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faBan, label: 'ยกเลิก' },
};

// สถานะ deposit (ในประวัติจับคู่) — ต่างจาก payout
const DEPOSIT_STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faHourglassHalf, label: 'รอชำระ' },
  paid: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  expired: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faTimesCircle, label: 'หมดอายุ' },
  expired_locked: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faLock, label: 'หมดอายุ (ล็อก)' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faBan, label: 'ยกเลิก' },
};

const fmt = (n: any) => `฿${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// ⭐ เงื่อนไข "พร้อม export ส่วนค้าง" — ต้องตรงกับ backend listExportableP2pPayouts (adminDepositController):
//    status ∈ pending/p2p_partial/expired + ไม่มี allocation ค้าง + มีส่วนค้าง + (pending ต้อง window หมดก่อน)
const isExportableAt = (p: AdminP2pPayout, at: number) =>
  ['pending', 'p2p_partial', 'expired'].includes(p.status)
  && Number(p.allocated_amount || 0) === 0
  && Math.round((Number(p.amount) - Number(p.funded_amount || 0)) * 100) / 100 > 0
  && (p.status !== 'pending' || (!!p.window_expires_at && new Date(p.window_expires_at).getTime() < at));

// ⭐ Timeline เหตุการณ์ P2P (tracking ละเอียด — admin เท่านั้น)
interface TimelineEvent {
  id: string;
  event_type: string;
  deposit_order_id?: string | null;
  payout_id?: string | null;
  from_status?: string | null;
  to_status?: string | null;
  amount?: number | null;
  actor?: string | null;
  reason?: string | null;
  created_at: string;
  order_number?: string | null;
  payout_number?: string | null;
  merchant?: { name: string } | null;
}

const EVENT_META: Record<string, { label: string; icon: IconDefinition; color: string; dot: string }> = {
  deposit_created:       { label: 'สร้างรายการฝาก',        icon: faPlusCircle,        color: 'text-sky-600 dark:text-sky-400',       dot: 'bg-sky-500' },
  deposit_matched:       { label: 'จับคู่กับ payout',        icon: faHandshake,         color: 'text-indigo-600 dark:text-indigo-400', dot: 'bg-indigo-500' },
  deposit_paid:          { label: 'ชำระเงินสำเร็จ',          icon: faCheckCircle,       color: 'text-green-600 dark:text-green-400',   dot: 'bg-green-500' },
  deposit_locked:        { label: 'หมดอายุ — ล็อกการจอง',   icon: faLock,              color: 'text-amber-600 dark:text-amber-400',   dot: 'bg-amber-500' },
  deposit_expired:       { label: 'หมดอายุ (ปล่อยจอง)',     icon: faTimesCircle,       color: 'text-gray-500 dark:text-slate-400',    dot: 'bg-gray-400' },
  deposit_released:      { label: 'แอดมินปล่อยการจอง',      icon: faUnlock,            color: 'text-purple-600 dark:text-purple-400', dot: 'bg-purple-500' },
  payout_created:        { label: 'สร้าง payout สภาพคล่อง',  icon: faMoneyBillTransfer, color: 'text-sky-600 dark:text-sky-400',       dot: 'bg-sky-500' },
  payout_funded:         { label: 'เติมยอด payout',          icon: faCoins,             color: 'text-green-600 dark:text-green-400',   dot: 'bg-green-500' },
  payout_completed:      { label: 'payout สำเร็จ',           icon: faFlagCheckered,     color: 'text-green-600 dark:text-green-400',   dot: 'bg-green-600' },
  payout_partial:        { label: 'รอเว็บโอนส่วนที่เหลือ',    icon: faClock,             color: 'text-indigo-600 dark:text-indigo-400', dot: 'bg-indigo-500' },
  payout_window_expired: { label: 'หมดเวลา window',          icon: faHourglassHalf,     color: 'text-amber-600 dark:text-amber-400',   dot: 'bg-amber-500' },
  payout_closed:         { label: 'ปิดงาน (เว็บโอน)',        icon: faFlagCheckered,     color: 'text-indigo-600 dark:text-indigo-400', dot: 'bg-indigo-600' },
  payout_cancelled:      { label: 'ยกเลิก payout',           icon: faBan,               color: 'text-gray-500 dark:text-slate-400',    dot: 'bg-gray-400' },
  deposit_cancelled:     { label: 'ยกเลิกรายการฝาก',         icon: faBan,               color: 'text-gray-500 dark:text-slate-400',    dot: 'bg-gray-400' },
};
const eventMeta = (t: string) => EVENT_META[t] || { label: t, icon: faCircleDot, color: 'text-gray-500 dark:text-slate-400', dot: 'bg-gray-400' };
const evTime = (s: string) => new Date(s).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

const AdminP2pPayoutsPage: React.FC = () => {
  const [rows, setRows] = useState<AdminP2pPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // ── ค้นหา (debounce) + กรองวันที่ (default ว่าง = แสดงทั้งหมด) + merchant ──
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [merchants, setMerchants] = useState<{ id: string; name: string; email: string }[]>([]);

  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchInput.trim()); setCurrentPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    merchantApi.get('/merchants').then((res) => setMerchants(res.data.data || [])).catch(() => {});
  }, []);

  const [completeModal, setCompleteModal] = useState<AdminP2pPayout | null>(null);
  const [completeNote, setCompleteNote] = useState('');
  const [completeSlip, setCompleteSlip] = useState<string | null>(null); // สลิปหลักฐานเว็บโอน (บังคับ)
  const [slipPreview, setSlipPreview] = useState<string | null>(null); // ดูสลิปที่โอนแล้ว (modal)
  const [submitting, setSubmitting] = useState(false);

  // ── ⭐ Export ส่วนค้าง (KTB Bulk / SCB Anywhere) — ติ๊กเลือกใบที่เข้าเงื่อนไขจากตารางหลัก แล้ว export shortfall ──
  const [exportModal, setExportModal] = useState(false);
  const [exportBanks, setExportBanks] = useState<any[]>([]); // ทุกธนาคาร active — filter ตาม format ตอน render
  const [exportFormat, setExportFormat] = useState<'ktb' | 'scb'>('ktb');
  const [exportBankId, setExportBankId] = useState('');
  const [exporting, setExporting] = useState(false);

  // เลือกใบที่จะ export — เก็บ snapshot ทั้งแถว (Map id → row) ให้เลือกข้ามหน้า pagination ได้
  const [selected, setSelected] = useState<Map<string, AdminP2pPayout>>(new Map());
  const selectedList = Array.from(selected.values());

  // บัญชีต้นทางตาม format ที่เลือก (KTB Bulk ↔ SCB Anywhere)
  const banksForFormat = (fmt: 'ktb' | 'scb', banks: any[]) =>
    banks.filter((b: any) => (b.bank_code || '').toUpperCase() === (fmt === 'ktb' ? 'KTB' : 'SCB') && b.is_active);
  const formatBanks = banksForFormat(exportFormat, exportBanks);

  // ── ⭐ ตาราง "Export แล้ว รอยืนยันโอน" — แสดงเสมอไม่ขึ้นกับ filter (เหมือนหน้า bulk) ──
  const [ptRows, setPtRows] = useState<AdminP2pPayout[]>([]);
  const ptAbortRef = useRef<AbortController | null>(null);
  const loadPendingTransfer = useCallback(async () => {
    if (ptAbortRef.current) ptAbortRef.current.abort();
    const controller = new AbortController();
    ptAbortRef.current = controller;
    try {
      const res = await merchantApi.get('/admin/payouts?is_p2p_liquidity=true&status=pending_transfer&limit=200&offset=0', { signal: controller.signal });
      const body = res.data?.data;
      if (!controller.signal.aborted) setPtRows(Array.isArray(body) ? body : (body?.payouts || []));
    } catch { /* ส่วนเสริม — ไม่ toast รบกวน */ }
  }, []);
  useEffect(() => { loadPendingTransfer(); }, [loadPendingTransfer]);
  const shortfallOf = (p: AdminP2pPayout) => Math.max(0, Number(p.amount) - Number(p.funded_amount || 0));

  // ── ตัวกรอง + collapse ของตาราง "รอยืนยันโอน" (เป็นของตัวเอง แยกจาก list หลัก) ──
  const [ptSearch, setPtSearch] = useState('');
  const [collapsedBatches, setCollapsedBatches] = useState<Set<string>>(new Set());
  const toggleBatch = (ref: string) => setCollapsedBatches((prev) => {
    const n = new Set(prev); n.has(ref) ? n.delete(ref) : n.add(ref); return n;
  });
  const ptFiltered = ptRows.filter((p) => {
    const q = ptSearch.trim().toLowerCase();
    if (!q) return true;
    return (p.payout_number || '').toLowerCase().includes(q)
      || (p.merchant?.name || '').toLowerCase().includes(q)
      || (p.bank_account_number || '').toLowerCase().includes(q)
      || (p.bank_account_name || '').toLowerCase().includes(q)
      || (p.transfer_response?.batch_ref || '').toLowerCase().includes(q);
  });

  const openExportModal = async () => {
    if (selected.size === 0) { toast.info('ติ๊กเลือกรายการที่จะ export จากตารางก่อน (เลือกได้เฉพาะใบที่มีเครื่องหมายถูก)'); return; }
    setExportModal(true);
    setExportBankId('');
    try {
      const banksRes = await merchantApi.get('/admin/banks');
      const banks = banksRes.data.data || [];
      setExportBanks(banks);
      const fmtBanks = banksForFormat(exportFormat, banks);
      if (fmtBanks.length === 1) setExportBankId(fmtBanks[0].id);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดบัญชีธนาคารไม่สำเร็จ');
    }
  };

  // สลับ format → เคลียร์บัญชีที่เลือก (คนละธนาคาร) + auto-select ถ้ามีบัญชีเดียว
  const handleFormatChange = (fmt: 'ktb' | 'scb') => {
    setExportFormat(fmt);
    const banks = banksForFormat(fmt, exportBanks);
    setExportBankId(banks.length === 1 ? banks[0].id : '');
  };

  const handleExport = async () => {
    if (!exportBankId) { toast.error(`กรุณาเลือกบัญชีต้นทาง ${exportFormat === 'ktb' ? 'KTB' : 'SCB'}`); return; }
    if (!selected.size) return;
    try {
      setExporting(true);
      if (exportFormat === 'ktb') {
        const res = await merchantApi.post('/admin/deposit-orders/p2p-payouts/export-ktb', {
          payout_ids: Array.from(selected.keys()),
          banks_id: exportBankId,
        });
        const d = res.data.data;
        // ดาวน์โหลดไฟล์ CSV (KTB gen ฝั่ง server)
        const blob = new Blob(['﻿' + d.csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = d.filename; a.click();
        URL.revokeObjectURL(url);
        toast.success(res.data.message);
      } else {
        // SCB Anywhere: server ล็อกใบ + คืนรายการ (ยอด = shortfall) → gen ไฟล์ TXT ฝั่ง client (util เดียวกับหน้า BulkTransfer)
        const res = await merchantApi.post('/admin/deposit-orders/p2p-payouts/export-scb', {
          payout_ids: Array.from(selected.keys()),
          banks_id: exportBankId,
        });
        const d = res.data.data;
        // ยอดในไฟล์ = ส่วนค้าง → map เป็น net_amount ให้ generator (txAmt อ่าน net_amount เท่านั้น)
        const filePayouts = (d.payouts || []).map((p: any) => ({ ...p, net_amount: p.shortfall }));
        downloadTextFile(generateScbFileAll(filePayouts, d.from_account), d.filename);
        toast.success(res.data.message);
      }
      setSelected(new Map()); // export แล้ว (ใบที่แพ้ race ถูกกรองออกโดย backend) → เคลียร์ selection
      setExportModal(false);
      loadData(false);
      loadPendingTransfer(); // ตาราง batch "รอยืนยันโอน" ด้านล่างอัปเดตทันที
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Export ไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  };

  // ยืนยันโอนทั้ง batch (ทุกใบใน batch_ref เดียวกัน) — ยืนยันรายใบเอาออกแล้ว ใช้ batch อย่างเดียว
  // (เอาใบที่ไม่ต้องการออกจากรอบด้วยปุ่ม "ยกเลิก" รายใบก่อนกดยืนยัน)
  const handleConfirmBatch = async (rows: AdminP2pPayout[]) => {
    if (!rows.length) return;
    try {
      setSubmitting(true);
      const res = await merchantApi.post('/admin/deposit-orders/p2p-payouts/confirm-transferred', { payout_ids: rows.map((p) => p.id) });
      toast.success(res.data.message || 'ปิดงานสำเร็จ');
      loadData(false);
      loadPendingTransfer();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ปิดงานไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelExport = async (p: AdminP2pPayout) => {
    try {
      setSubmitting(true);
      await merchantApi.post('/admin/deposit-orders/p2p-payouts/cancel-export', { payout_ids: [p.id] });
      toast.success(`ยกเลิก export ${p.payout_number} (คืนสถานะเดิม)`);
      loadData(false);
      loadPendingTransfer();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ยกเลิกไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  const [historyOf, setHistoryOf] = useState<AdminP2pPayout | null>(null);
  const [history, setHistory] = useState<PairRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [histTab, setHistTab] = useState<'pairs' | 'timeline'>('pairs');
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // ticker เบาๆ สำหรับ re-evaluate เงื่อนไขปุ่ม "ปิดงาน" เมื่อ window หมด (การแสดงผลใช้ CountdownTimer)
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  // showLoading=false = refresh เงียบ (ไม่โชว์ skeleton) — ใช้กับทุกเส้นที่ไม่ใช่ user เปลี่ยน filter/กด Refresh
  const abortRef = useRef<AbortController | null>(null);
  const loadData = useCallback(async (showLoading = true) => {
    // last-request-wins: ยกเลิก request ก่อนหน้า กัน response เก่ามาทับข้อมูลใหม่ (pattern เดียวกับ AllOrdersPage)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (showLoading) setLoading(true);
      const params = new URLSearchParams();
      params.append('is_p2p_liquidity', 'true');
      // pending_transfer อยู่ตาราง "รอยืนยันโอน" แยกด้านล่าง — ไม่โผล่ใน list หลัก
      params.append('exclude_status', 'pending_transfer');
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedMerchantId) params.append('merchant_id', selectedMerchantId);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));
      const res = await merchantApi.get(`/admin/payouts?${params.toString()}`, { signal: controller.signal });
      if (!controller.signal.aborted) {
        const body = res.data?.data;
        let list: AdminP2pPayout[] = Array.isArray(body) ? body : (body?.payouts || []);
        // กัน race: response ที่ DB snapshot ก่อนแถวเพิ่ง prepend จาก socket จะไม่มีแถวนั้น — ใส่กลับ (event ไม่ยิงซ้ำ)
        const nowMs = Date.now();
        recentPrependsRef.current.forEach((v, k) => { if (nowMs - v.at > 15000) recentPrependsRef.current.delete(k); });
        if (currentPage === 1 && !searchTerm && (!statusFilter || statusFilter === 'pending')) {
          const missing = Array.from(recentPrependsRef.current.values())
            .filter((v) => !list.some((r) => r.id === v.row.id)).map((v) => v.row);
          if (missing.length) list = [...missing, ...list].slice(0, pageSize);
        }
        setRows(list);
        setTotal(res.data?.total || body?.total || list.length);
        // sync selection กับข้อมูลสด: ใบที่หลุดเงื่อนไข export แล้ว (สถานะเปลี่ยน/มี allocation) → เอาออก, ที่เหลือ refresh snapshot
        setSelected((prev) => {
          if (prev.size === 0) return prev;
          const m = new Map(prev);
          const at = Date.now();
          list.forEach((p) => {
            if (!m.has(p.id)) return;
            if (isExportableAt(p, at)) m.set(p.id, p);
            else m.delete(p.id);
          });
          return m;
        });
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
        toast.error(e.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ');
      }
    } finally {
      // เคลียร์เสมอเมื่อไม่ถูก abort (ไม่ดูแค่ showLoading) — silent call ที่ abort โหลดแบบ skeleton ต้องเป็นคนปิด loading แทน
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [statusFilter, searchTerm, selectedMerchantId, appliedDateFrom, appliedDateTo, currentPage, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── ⭐ Live mode (pattern AllOrdersPage): event → merge/prepend เข้า state ตรง ๆ ไม่ refetch → ตารางไม่กระพริบ ──
  const [realtimeMode, setRealtimeMode] = useState(true);
  const [bufferedCount, setBufferedCount] = useState(0);
  // แถวที่เพิ่ง prepend จาก socket (id → row) — ให้ loadData ใส่กลับถ้า response เก่ากว่าแถว (ดูใน loadData)
  const recentPrependsRef = useRef<Map<string, { row: AdminP2pPayout; at: number }>>(new Map());

  // state ปัจจุบันผ่าน ref — handler ใช้เช็ค membership โดยไม่ต้องใส่ rows/ptRows ใน deps
  const rowsRef = useRef(rows);
  const ptRowsRef = useRef(ptRows);
  useEffect(() => { rowsRef.current = rows; ptRowsRef.current = ptRows; }, [rows, ptRows]);

  // loaders ผ่าน ref — socket effect ใช้ deps นิ่ง ไม่ re-subscribe ตาม identity ของ loader
  const loadDataRef = useRef(loadData);
  const loadPendingTransferRef = useRef(loadPendingTransfer);
  useEffect(() => { loadDataRef.current = loadData; loadPendingTransferRef.current = loadPendingTransfer; }, [loadData, loadPendingTransfer]);

  // fallback refetch เงียบ (debounce 600ms) — เฉพาะ event ที่ payload ไม่พอ merge (backend เก่า / batch payload)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      loadDataRef.current(false);
      loadPendingTransferRef.current();
    }, 600);
  }, []);
  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    abortRef.current?.abort();
    ptAbortRef.current?.abort();
  }, []);

  const toggleRealtimeMode = () => {
    if (realtimeMode) {
      // กำลังพัก live — ยกเลิก refetch เงียบที่ค้างคิว ไม่ให้ตารางขยับเองใน View mode
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    } else if (bufferedCount > 0) {
      loadData(false); loadPendingTransfer();
      setBufferedCount(0);
      toast.info(`อัพเดท ${bufferedCount} รายการล่าสุด`);
    }
    setRealtimeMode((v) => !v);
  };

  const handleSearch = () => {
    if (dateFrom && dateTo && new Date(dateFrom).getTime() > new Date(dateTo).getTime()) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด'); return;
    }
    setAppliedDateFrom(dateFrom); setAppliedDateTo(dateTo); setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchInput(''); setSearchTerm('');
    setDateFrom(today()); setDateTo(today()); setAppliedDateFrom(today()); setAppliedDateTo(today());
    setSelectedMerchantId(''); setStatusFilter(''); setCurrentPage(1);
  };

  const loadTimeline = useCallback(async (payoutId: string) => {
    try {
      setTimelineLoading(true);
      const res = await merchantApi.get(`/admin/deposit-orders/timeline?payout_id=${payoutId}`);
      if (res.data.success) setTimeline(res.data.data || []);
    } catch {
      /* timeline เป็นข้อมูลเสริม — ไม่ต้อง toast รบกวน */
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  const openHistory = async (p: AdminP2pPayout) => {
    setHistoryOf(p);
    setHistory([]);
    setTimeline([]);
    setHistTab('pairs');
    loadTimeline(p.id);
    try {
      setHistoryLoading(true);
      const res = await merchantApi.get(`/admin/deposit-orders/pair-history/${p.id}`);
      if (res.data.success) setHistory(res.data.data || []);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดประวัติไม่สำเร็จ');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ⭐ realtime: refresh timeline เมื่อมี event ใหม่ (เฉพาะตอนเปิด modal ประวัติ)
  useEffect(() => {
    if (!historyOf) return;
    const refresh = () => loadTimeline(historyOf.id);
    socketService.on('p2p-event', refresh);
    socketService.on('p2p-events-updated', refresh);
    return () => {
      socketService.off('p2p-event', refresh);
      socketService.off('p2p-events-updated', refresh);
    };
  }, [historyOf, loadTimeline]);

  // ⭐ realtime ตารางหลัก + ตารางรอยืนยันโอน: merge/prepend จาก payload ตรง ๆ (ไม่ refetch — ตารางไม่กระพริบ)
  //    payload บาง (backend เก่า / batch) → ตกไป scheduleSilentRefresh แทน
  useEffect(() => {
    // แถวใหม่เกิดเป็น pending เสมอ — prepend เฉพาะตอน filter ปัจจุบันมองเห็นมัน (จำลองเงื่อนไข query ฝั่ง server)
    const createdPassesFilters = (merchantId: string) => {
      const todayStr = today();
      return (!statusFilter || statusFilter === 'pending')
        && !searchTerm
        && (!selectedMerchantId || selectedMerchantId.split(',').includes(merchantId))
        && (!appliedDateFrom || appliedDateFrom <= todayStr)
        && (!appliedDateTo || appliedDateTo >= todayStr);
    };

    const onPayoutCreated = (data: any) => {
      // ⭐ กรองเฉพาะ event ของ P2P liquidity — payout ปกติทั้งเว็บยิง event ถี่มาก ไม่เกี่ยวกับหน้านี้
      if (data?.is_p2p_liquidity !== true) return;
      if (!realtimeMode) { setBufferedCount((c) => c + 1); return; }
      const row: AdminP2pPayout | undefined = data?.row;
      if (row?.id && row.payout_number) {
        if (!createdPassesFilters(row.merchant?.id || data.merchant_id)) return; // server ก็ไม่คืนแถวนี้ในมุมมองปัจจุบัน
        recentPrependsRef.current.set(row.id, { row, at: Date.now() }); // ให้ refetch ที่ snapshot เก่ากว่าใส่แถวกลับ
        setCurrentPage((prevPage) => {
          if (prevPage === 1) {
            setRows((prev) => (prev.some((r) => r.id === row.id) ? prev : [row, ...prev.slice(0, pageSize - 1)]));
          }
          return prevPage;
        });
        setTotal((t) => t + 1);
      } else {
        scheduleSilentRefresh(); // backend เก่ายังไม่แนบ row
      }
    };

    const onPayoutUpdated = (data: any) => {
      if (data?.is_p2p_liquidity !== true) return;
      if (!realtimeMode) { setBufferedCount((c) => c + 1); return; }
      if (!data.payout_id) { scheduleSilentRefresh(); return; } // aggregate/batch payload (เช่น export/cancel-export) — ระบุแถวไม่ได้
      const inMain = rowsRef.current.some((r) => r.id === data.payout_id);
      const inPt = ptRowsRef.current.some((r) => r.id === data.payout_id);
      if (!inMain && !inPt) { scheduleSilentRefresh(); return; } // แถวไม่อยู่ในมุมมอง — ให้ refetch เงียบตัดสิน (กันแถวหาย/ยอดค้าง)
      const patch: Partial<AdminP2pPayout> = {};
      if (data.status != null) patch.status = data.status;
      if (data.allocated_amount != null) patch.allocated_amount = Number(data.allocated_amount);
      if (data.funded_amount != null) patch.funded_amount = Number(data.funded_amount);
      setRows((prev) => prev.map((r) => (r.id === data.payout_id ? { ...r, ...patch } : r)));
      // panel รอยืนยันโอน: merge แล้วเอาแถวที่พ้น pending_transfer ออก (confirm-transferred → p2p_completed)
      setPtRows((prev) => prev
        .map((r) => (r.id === data.payout_id ? { ...r, ...patch } : r))
        .filter((r) => r.status === 'pending_transfer'));
      // checkbox export ที่ติ๊กไว้: ใบที่หลุดเงื่อนไขหลัง merge → เอาออกทันที ไม่รอ refetch
      setSelected((prev) => {
        if (!prev.has(data.payout_id)) return prev;
        const m = new Map(prev);
        const merged = { ...m.get(data.payout_id)!, ...patch };
        if (isExportableAt(merged, Date.now())) m.set(data.payout_id, merged);
        else m.delete(data.payout_id);
        return m;
      });
      // แถวหลุดจาก panel (เช่น confirm-transferred) หรือหลุด tab สถานะปัจจุบัน → refetch เงียบให้ view ตรงกับ server
      const leftPt = inPt && data.status != null && data.status !== 'pending_transfer';
      const leftFilter = inMain && !!statusFilter && data.status != null && data.status !== statusFilter;
      if (leftPt || leftFilter) scheduleSilentRefresh();
    };

    socketService.on('payout-created', onPayoutCreated);
    socketService.on('payout-updated', onPayoutUpdated);
    return () => {
      socketService.off('payout-created', onPayoutCreated);
      socketService.off('payout-updated', onPayoutUpdated);
    };
  }, [realtimeMode, statusFilter, searchTerm, selectedMerchantId, appliedDateFrom, appliedDateTo, pageSize, scheduleSilentRefresh]);

  const handleComplete = async () => {
    if (!completeModal) return;
    if (!completeSlip) { toast.error('กรุณาแนบสลิปหลักฐานการโอนส่วนที่เหลือ'); return; }
    try {
      setSubmitting(true);
      const res = await merchantApi.patch(`/admin/deposit-orders/payout/${completeModal.id}/complete-partial`, { note: completeNote || undefined, imageBase64: completeSlip });
      if (res.data.success) {
        toast.success('ปิดงาน payout สำเร็จ (เว็บโอนส่วนที่เหลือแล้ว)');
        setCompleteModal(null); setCompleteNote(''); setCompleteSlip(null); loadData(false);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ปิดงานไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  const isCloseable = (p: AdminP2pPayout) =>
    p.status === 'p2p_partial' || p.status === 'expired'
    || (p.status === 'pending' && !!p.window_expires_at && new Date(p.window_expires_at).getTime() < nowTick);

  // nowTick → checkbox เปิดให้ติ๊กทันทีที่ window หมด โดยไม่ต้อง refresh
  const isExportable = (p: AdminP2pPayout) => isExportableAt(p, nowTick);

  // ── checkbox เลือกใบ export (ติ๊กได้เฉพาะใบที่เข้าเงื่อนไข) ──
  const toggleSelect = (p: AdminP2pPayout) => setSelected((prev) => {
    const m = new Map(prev);
    if (m.has(p.id)) m.delete(p.id); else m.set(p.id, p);
    return m;
  });
  const exportableOnPage = rows.filter(isExportable);
  const allPageSelected = exportableOnPage.length > 0 && exportableOnPage.every((p) => selected.has(p.id));
  const toggleSelectPage = () => setSelected((prev) => {
    const m = new Map(prev);
    if (allPageSelected) exportableOnPage.forEach((p) => m.delete(p.id));
    else exportableOnPage.forEach((p) => m.set(p.id, p));
    return m;
  });

  const columns: Column<AdminP2pPayout>[] = [
    {
      key: 'select', width: '40px',
      header: (
        <input
          type="checkbox"
          checked={allPageSelected}
          disabled={exportableOnPage.length === 0}
          onChange={toggleSelectPage}
          className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 accent-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
          title="เลือกทั้งหมดที่เข้าเงื่อนไข export ในหน้านี้"
        />
      ),
      render: (p) => (
        <input
          type="checkbox"
          checked={selected.has(p.id)}
          disabled={!isExportable(p)}
          onChange={() => toggleSelect(p)}
          className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 accent-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
          title={isExportable(p) ? 'เข้าเงื่อนไข export ส่วนค้าง — ติ๊กเพื่อเลือก' : 'ไม่เข้าเงื่อนไข export (ต้องมีส่วนค้าง, ไม่มี allocation ค้าง, window หมด)'}
        />
      ),
    },
    {
      key: 'payout_number', header: 'เลขที่', width: '240px',
      // แสดงเลขเต็มไม่ตัด (เหมือนหน้า payout ปกติ)
      render: (p) => <code className="text-xs whitespace-nowrap bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded">{p.payout_number}</code>,
    },
    {
      key: 'merchant', header: 'ผู้ถอน', width: '180px',
      render: (p) => (
        <div className="min-w-0">
          <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{p.merchant?.name || '-'}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{p.merchant?.email}</p>
        </div>
      ),
    },
    {
      key: 'bank', header: 'บัญชีปลายทาง', width: '170px',
      render: (p) => (
        <div className="min-w-0">
          <span className="block truncate max-w-[160px] text-sm text-gray-700 dark:text-slate-200">{p.bank_account_name || '-'}</span>
          <span className="text-[10px] text-gray-400 dark:text-slate-500">{p.bank_name} · {p.bank_account_number}</span>
        </div>
      ),
    },
    {
      key: 'amount', header: 'ยอด', width: '110px',
      render: (p) => <span className="font-bold text-sm tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmt(p.amount)}</span>,
    },
    {
      key: 'funded', header: 'เติม / เหลือ', width: '170px',
      render: (p) => {
        const amount = Number(p.amount) || 0;
        const funded = Number(p.funded_amount || 0);
        const allocated = Number(p.allocated_amount || 0);
        const remaining = Math.max(0, amount - funded - allocated);
        const pct = (v: number) => (amount > 0 ? Math.min(100, (v / amount) * 100) : 0);
        return (
          <div className="min-w-[140px]">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
              <div className="bg-green-500 transition-all" style={{ width: `${pct(funded)}%` }} title={`เติมแล้ว ${fmt(funded)}`} />
              <div className="bg-amber-400 transition-all" style={{ width: `${pct(allocated)}%` }} title={`รอยืนยัน ${fmt(allocated)}`} />
            </div>
            <div className="mt-1 flex justify-between text-[10px] tabular-nums leading-tight">
              <span className="text-green-600 dark:text-green-400">เติม {fmt(funded)}</span>
              <span className="text-gray-500 dark:text-slate-400">เหลือ {fmt(remaining)}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'awaiting', header: 'รอยืนยัน', width: '110px',
      render: (p) => (
        (p.allocated_amount || 0) > 0
          ? <span className="tabular-nums text-sm font-medium text-amber-600 dark:text-amber-400" title="ยอดที่จองค้าง (รอโอน + หมดอายุล็อก)"><FontAwesomeIcon icon={faLock} className="mr-1 text-[10px]" />{fmt(p.allocated_amount)}</span>
          : <span className="text-gray-300 dark:text-slate-600">—</span>
      ),
    },
    {
      key: 'fee', header: 'ค่าธรรมเนียม', width: '120px',
      render: (p) => {
        const { pct, fee } = payoutFeeOf(p);
        return <span className="text-xs tabular-nums text-amber-600 dark:text-amber-400">{fmt(fee)} <span className="text-gray-400 dark:text-slate-500">({pct}%)</span></span>;
      },
    },
    {
      key: 'window', header: 'หมดเวลาใน', width: '120px',
      render: (p) => (
        p.status === 'pending' && p.window_expires_at
          ? <span className="tabular-nums text-sm font-medium" style={{ color: 'var(--theme-accent)' }}><FontAwesomeIcon icon={faClock} className="mr-1" /><CountdownTimer expiresAt={p.window_expires_at} /></span>
          : <span className="text-gray-300 dark:text-slate-600">—</span>
      ),
    },
    {
      key: 'status', header: 'สถานะ', width: '180px',
      render: (p) => {
        // ⭐ ใบสำเร็จแยกวิธีปิดงาน: มี transfer_response (export KTB/SCB) หรือ slip_url (เว็บโอน+สลิป) = แอดมินปิด, ไม่งั้น = peer เติมครบ
        //    (funded แยกไม่ได้เพราะ bump = amount ตอนปิดทุกแบบ)
        const isAdminClosed = p.status === 'p2p_completed' && (!!p.transfer_response || !!p.slip_url);
        const isPeerFull = p.status === 'p2p_completed' && !isAdminClosed;
        return (
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-1.5">
              {(p.allocated_amount || 0) > 0 && <FontAwesomeIcon icon={faLock} className="text-[10px] text-amber-500" title="มี deposit จองค้าง" />}
              <StatusBadge status={p.status} statusMap={STATUS_MAP} />
            </div>
            {/* วิธีปิดงาน — อยู่ใต้ badge สำเร็จ */}
            {isPeerFull && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400" title="ปิดงานได้ยอดครบจากการฝาก (peer) ไม่ต้องเว็บโอน">
                เติมครบ (Peer)
              </span>
            )}
            {isAdminClosed && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400" title="แอดมินปิดงาน (เว็บโอนส่วนที่เหลือ — export KTB/SCB หรือแนบสลิป)">
                Admin (Export)
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'slip', header: 'สลิปโอน', width: '80px',
      render: (p) => (
        p.slip_url
          ? <button type="button" onClick={(e) => { e.stopPropagation(); setSlipPreview(p.slip_url!); }} title="ดูสลิปหลักฐานการโอน">
              <img src={p.slip_url} alt="slip" className="h-10 w-10 rounded-md object-cover border border-gray-200 dark:border-slate-600 hover:ring-2 hover:ring-indigo-400 transition cursor-pointer" />
            </button>
          : <span className="text-gray-300 dark:text-slate-600">—</span>
      ),
    },
    {
      key: 'actions', header: 'จัดการ', width: '210px',
      render: (p) => (
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={() => openHistory(p)}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors">
            <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1" />ประวัติ
          </button>
          {isCloseable(p) && (
            <button onClick={() => { setCompleteModal(p); setCompleteNote(''); setCompleteSlip(null); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />ปิดงาน (เว็บโอน)
            </button>
          )}
          {/* pending_transfer ไม่โผล่ใน list หลักแล้ว (อยู่ตาราง "รอยืนยันโอน" ด้านล่าง) — ไม่ต้องมีปุ่มที่นี่ */}
        </div>
      ),
    },
  ];

  // ไม่มี pending_transfer ใน tab หลัก — อยู่ตาราง "รอยืนยันโอน" แยกด้านล่าง
  const STATUS_OPTS = ['', 'pending', 'p2p_partial', 'p2p_completed', 'expired', 'cancelled'];

  return (
    <>
      <UiPageHeader
        icon={faArrowRightArrowLeft}
        title="P2P Payouts (สภาพคล่อง)"
        subtitle={`จัดการรายการถอน P2P · ปิดงานเมื่อเว็บโอนส่วนที่เหลือ • ${total.toLocaleString()} รายการ`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <UiButton icon={faFileExport} onClick={openExportModal}>
              Export ส่วนค้าง (KTB/SCB){selected.size > 0 ? ` · ${selected.size} ใบ` : ''}
            </UiButton>
            <UiButton icon={faRotate} onClick={() => { loadData(); loadPendingTransfer(); }}>Refresh</UiButton>
          </div>
        }
        below={<LiveViewToggle realtimeMode={realtimeMode} onToggle={toggleRealtimeMode} />}
      />

      {/* ค้นหา + กรองวันที่ */}
      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><FontAwesomeIcon icon={faSearch} className="mr-1" />ค้นหา</label>
          <div className="relative">
            <input
              type="text" placeholder="เลขที่ payout / ชื่อบัญชี / เลขบัญชี..." value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 pr-7"
            />
            {searchInput && <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} className="text-xs" /></button>}
          </div>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ผู้ถอน (Merchant)</label>
          <MerchantMultiSelect merchants={merchants} value={selectedMerchantId} onChange={(v) => { setSelectedMerchantId(v); setCurrentPage(1); }} variant="light" />
        </div>
      </UiFilterPanel>

      {/* Status tabs */}
      <div className="w-fit shrink-0 mb-4">
        <div className="inline-flex items-stretch h-10 gap-0.5 border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 w-fit flex-wrap">
          {STATUS_OPTS.map((s) => {
            const active = statusFilter === s;
            return (
              <button key={s || 'all'} type="button" onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                className={`inline-flex items-center px-3 h-full rounded-md text-sm font-medium whitespace-nowrap transition-all
                  ${active
                    ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm'
                    : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'}`}>
                {s === '' ? 'ทั้งหมด' : (STATUS_MAP[s]?.label || s)}
              </button>
            );
          })}
        </div>
      </div>

      {/* แถบสรุปที่ติ๊กเลือกไว้ — export เฉพาะที่เลือก */}
      {selected.size > 0 && (
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2 rounded-xl border border-green-200 dark:border-green-800 bg-green-50/60 dark:bg-green-900/10 px-4 py-2.5">
          <p className="text-sm font-medium text-green-700 dark:text-green-300">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5" />
            เลือกไว้ {selected.size} รายการ · รวมส่วนค้าง {fmt(selectedList.reduce((s, p) => s + shortfallOf(p), 0))}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Map())}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors">
              <FontAwesomeIcon icon={faTimes} className="mr-1" />ล้างที่เลือก
            </button>
            <button onClick={openExportModal}
              className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
              <FontAwesomeIcon icon={faFileExport} className="mr-1" />Export ที่เลือก ({selected.size})
            </button>
          </div>
        </div>
      )}

      {/* buffer โหมด View — กดเพื่อดึงรายการที่ค้างระหว่างพัก live */}
      <LiveModeBufferBadge
        count={bufferedCount}
        onFlush={() => { loadData(false); loadPendingTransfer(); setBufferedCount(0); toast.success('อัพเดทข้อมูลแล้ว'); }}
      />

      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        serverPagination={{ currentPage, totalItems: total, pageSize, onPageChange: setCurrentPage, onPageSizeChange: setPageSize }}
      />

      {/* ⭐ ตาราง batch "Export แล้ว รอยืนยันโอน" — ตัวกรอง + confirm เป็นกลุ่ม + พับได้ (เหมือนหน้า BulkTransfer) */}
      {ptRows.length > 0 && (
        <div className="mt-4 mb-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">
              <FontAwesomeIcon icon={faFileExport} className="mr-1.5" />
              Export แล้ว รอยืนยันโอน (P2P) · {ptFiltered.length}/{ptRows.length} รายการ · รวมส่วนค้าง {fmt(ptFiltered.reduce((s, p) => s + shortfallOf(p), 0))}
            </p>
            {/* ตัวกรองเป็นของตัวเอง — แยกจาก list หลัก */}
            <div className="relative w-full sm:w-72">
              <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
              <input value={ptSearch} onChange={(e) => setPtSearch(e.target.value)}
                placeholder="ค้นหา batch / payout / ผู้ถอน / บัญชี..."
                className="w-full pl-8 pr-7 py-1.5 text-xs border border-blue-200 dark:border-blue-800 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900" />
              {ptSearch && <button type="button" onClick={() => setPtSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} className="text-xs" /></button>}
            </div>
          </div>
          {ptFiltered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-slate-500">ไม่พบรายการที่ตรงกับตัวกรอง</div>
          ) : (() => {
            // จัดกลุ่มตาม batch_ref (export คนละรอบ = คนละไฟล์)
            const groups = new Map<string, AdminP2pPayout[]>();
            ptFiltered.forEach((p) => {
              const key = p.transfer_response?.batch_ref || '-';
              groups.set(key, [...(groups.get(key) || []), p]);
            });
            return Array.from(groups.entries()).map(([batchRef, list]) => {
              const collapsed = collapsedBatches.has(batchRef);
              return (
                <div key={batchRef} className="border-b border-blue-100 dark:border-blue-800 last:border-b-0">
                  <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2 bg-white/60 dark:bg-slate-800/40">
                    <button type="button" onClick={() => toggleBatch(batchRef)} className="flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-slate-400 min-w-0 text-left">
                      <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} className="text-[10px] text-blue-500 shrink-0" />
                      <code className="text-[11px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-mono">{batchRef}</code>
                      <span className="font-medium text-gray-600 dark:text-slate-300">{list.length} ใบ · {fmt(list.reduce((s, p) => s + shortfallOf(p), 0))}</span>
                      {list[0]?.transfer_response?.filename && <span className="truncate max-w-[200px]">{list[0].transfer_response.filename}</span>}
                      {list[0]?.transfer_response?.exported_at && <span>· {evTime(list[0].transfer_response.exported_at)}</span>}
                      {list[0]?.transfer_response?.exported_by_name && <span>· โดย {list[0].transfer_response.exported_by_name}</span>}
                    </button>
                    <button onClick={() => handleConfirmBatch(list)} disabled={submitting}
                      className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 shrink-0">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />ยืนยันโอนทั้ง batch ({list.length})
                    </button>
                  </div>
                  {!collapsed && (
                    <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-xs">
                      <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400">
                        <tr>
                          <th className="px-3 py-2 text-left">เลขที่</th>
                          <th className="px-3 py-2 text-left">ผู้ถอน</th>
                          <th className="px-3 py-2 text-left">บัญชีปลายทาง</th>
                          <th className="px-3 py-2 text-right">ยอดเต็ม</th>
                          <th className="px-3 py-2 text-right">เติมแล้ว</th>
                          <th className="px-3 py-2 text-right">ส่วนค้าง (โอน)</th>
                          <th className="px-3 py-2 text-right">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                        {list.map((p) => (
                          <tr key={p.id}>
                            <td className="px-3 py-2"><code className="text-[11px] bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">{p.payout_number}</code></td>
                            <td className="px-3 py-2 truncate max-w-[140px] text-gray-700 dark:text-slate-200">{p.merchant?.name || '-'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-slate-300">
                              <span className="block truncate max-w-[160px]">{p.bank_account_name}</span>
                              <span className="text-[10px] text-gray-400 dark:text-slate-500">{p.bank_name} · {p.bank_account_number}</span>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{fmt(p.amount)}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-green-600 dark:text-green-400">{fmt(p.funded_amount)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-bold text-blue-600 dark:text-blue-400">{fmt(shortfallOf(p))}</td>
                            <td className="px-3 py-2">
                              {/* ยืนยันโอนใช้ปุ่ม batch อย่างเดียว — รายใบเหลือแค่ยกเลิก (เอาใบออกจากรอบโอน) */}
                              <div className="flex items-center justify-end">
                                <button onClick={() => handleCancelExport(p)} disabled={submitting}
                                  className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50">
                                  <FontAwesomeIcon icon={faTimes} className="mr-1" />ยกเลิก
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ปิดงาน */}
      <UiModal
        open={!!completeModal}
        onClose={() => { setCompleteModal(null); setCompleteNote(''); setCompleteSlip(null); }}
        title="ปิดงาน Payout (เว็บโอนส่วนที่เหลือ)"
        icon={faCheckCircle}
        iconColor="text-indigo-500"
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => { setCompleteModal(null); setCompleteNote(''); setCompleteSlip(null); }} disabled={submitting} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton onClick={handleComplete} loading={submitting} disabled={!completeSlip} className="flex-1 justify-center">ยืนยันปิดงาน</UiButton>
          </div>
        }
      >
        {completeModal && (
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">Payout:</span><strong className="text-gray-800 dark:text-slate-100">{completeModal.payout_number}</strong></div>
              {/* บัญชีที่เว็บต้องโอนเข้า — จุดสำคัญที่สุดของ modal นี้ */}
              <div className="flex justify-between gap-3"><span className="text-gray-500 dark:text-slate-400 shrink-0">โอนเข้าบัญชี:</span>
                <span className="text-right">
                  <strong className="block text-gray-800 dark:text-slate-100">{completeModal.bank_account_name || '-'}</strong>
                  <span className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">{completeModal.bank_name} · {completeModal.bank_account_number}</span>
                </span>
              </div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">ยอดรวม:</span><strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmt(completeModal.amount)}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">เติมแล้ว (P2P):</span><span className="text-green-600 dark:text-green-400 tabular-nums">{fmt(completeModal.funded_amount)}</span></div>
              {(completeModal.allocated_amount || 0) > 0 && (
                <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400"><FontAwesomeIcon icon={faLock} className="mr-1 text-[10px] text-amber-500" />deposit ล็อก/รอโอน:</span><span className="text-amber-600 dark:text-amber-400 tabular-nums">{fmt(completeModal.allocated_amount)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">ว่าง (ยังไม่จับคู่):</span><span className="text-gray-600 dark:text-slate-300 tabular-nums">{fmt(Math.max(0, Number(completeModal.amount) - Number(completeModal.funded_amount || 0) - Number(completeModal.allocated_amount || 0)))}</span></div>
              <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-slate-600"><span className="text-gray-500 dark:text-slate-400">เว็บต้องโอน (ถ้าปิดเลย):</span><strong className="text-amber-600 dark:text-amber-400 tabular-nums">{fmt(Number(completeModal.amount) - Number(completeModal.funded_amount || 0))}</strong></div>
            </div>
            {(completeModal.allocated_amount || 0) > 0 && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-700 dark:text-amber-300">
                <FontAwesomeIcon icon={faLock} className="mr-1" />
                payout นี้มี deposit ล็อก {fmt(completeModal.allocated_amount)} — ถ้า deposit นั้น<strong>โอนจริง</strong> ให้ปิดหน้านี้แล้วไป "อนุมัติ (แนบสลิป)" ก่อน เพื่อไม่ให้ผู้ถอนได้เกิน · ถ้า<strong>ไม่โอน</strong> การปิดงานจะปล่อย deposit นั้นแล้วเว็บจ่ายแทน
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-slate-400">ยืนยันว่าเว็บได้โอนส่วนที่เหลือให้ผู้ถอนแล้ว → ปิดงาน + หักค่าธรรมเนียมถอนจากกระเป๋าเครดิต fee</p>
            {/* บอกผลลัพธ์ให้ชัดก่อนกด (ทรงเดียวกับหน้า bulk-transfer) */}
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-xs text-amber-700 dark:text-amber-400">
              <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 shrink-0" />
              <span>สถานะจะปรับเป็น <strong>สำเร็จ (completed)</strong> ทันที และระบบจะส่ง <strong>Webhook payout.completed</strong> แจ้งร้านค้า (พร้อมยอดที่ชำระ/คงเหลือ) — บันทึกชื่อแอดมินผู้ทำรายการลงใบ payout</span>
            </div>
            <UiInput label="หมายเหตุ (ถ้ามี)" value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} placeholder="เลขอ้างอิงการโอน ฯลฯ" />

            {/* ⭐ บังคับแนบสลิปหลักฐานเว็บโอนส่วนที่เหลือ */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1">
                แนบสลิปการโอน <span className="text-red-500">*</span>
                <span className="text-gray-400 dark:text-slate-500 font-normal"> (หลักฐานเว็บโอนส่วนที่เหลือ)</span>
              </label>
              {completeSlip ? (
                <div className="relative inline-block">
                  <img src={completeSlip} alt="slip" className="max-h-44 rounded-lg border border-gray-200 dark:border-slate-600" />
                  <button type="button" onClick={() => setCompleteSlip(null)}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs shadow-md">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl py-6 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 text-gray-400 dark:text-slate-500">
                  <FontAwesomeIcon icon={faUpload} className="text-lg" />
                  <span className="text-xs">คลิกเพื่อเลือกรูปสลิป</span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast.error('ไฟล์ใหญ่เกิน 5MB'); return; }
                      const reader = new FileReader();
                      reader.onload = () => setCompleteSlip(reader.result as string);
                      reader.readAsDataURL(file);
                    }} />
                </label>
              )}
            </div>
          </div>
        )}
      </UiModal>

      {/* ดูสลิปหลักฐานการโอน (คลิกจากคอลัมน์ "สลิปโอน") */}
      <UiModal
        open={!!slipPreview}
        onClose={() => setSlipPreview(null)}
        title="สลิปหลักฐานการโอน"
        icon={faFileExport}
        iconColor="text-indigo-500"
        size="md"
      >
        {slipPreview && (
          <div className="flex justify-center">
            <img src={slipPreview} alt="สลิปหลักฐานการโอน" className="max-h-[70vh] w-auto rounded-lg border border-gray-200 dark:border-slate-600" />
          </div>
        )}
      </UiModal>

      {/* ⭐ Export ส่วนค้าง (KTB Bulk / SCB Anywhere) */}
      <UiModal
        open={exportModal}
        onClose={() => !exporting && setExportModal(false)}
        title="Export ส่วนค้าง P2P"
        icon={faFileExport}
        iconColor="text-blue-500"
        size="lg"
        scrollable
        footer={
          <div className="flex gap-2 w-full">
            <UiButton variant="secondary" onClick={() => setExportModal(false)} disabled={exporting} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton onClick={handleExport} loading={exporting} disabled={!selected.size || !exportBankId} className="flex-1 justify-center">
              Export {exportFormat === 'ktb' ? 'KTB' : 'SCB'} {selected.size} รายการ ({fmt(selectedList.reduce((s, p) => s + shortfallOf(p), 0))})
            </UiButton>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-xs text-gray-500 dark:text-slate-400">
            รายการที่<strong>ติ๊กเลือกจากตาราง</strong> (เลือกได้เฉพาะใบที่ยังมีส่วนค้าง + ไม่มี deposit ค้างจับคู่) — ยอดในไฟล์ = <strong>ส่วนค้าง</strong> (ยอดเต็ม − เติมแล้ว)
            หลัง export สถานะจะเป็น "รอยืนยันโอน" (หยุดจับคู่) และเมื่อโอนจริงแล้วกด "ยืนยันโอนแล้ว" เพื่อปิดงาน + หักค่าธรรมเนียม
          </p>
          {/* format toggle: KTB Bulk / SCB Anywhere */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">รูปแบบไฟล์</label>
            <div className="inline-flex items-stretch h-9 gap-0.5 border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5">
              {([['ktb', 'KTB Bulk (CSV)'], ['scb', 'SCB Anywhere (TXT)']] as const).map(([k, label]) => (
                <button key={k} type="button" onClick={() => handleFormatChange(k)} disabled={exporting}
                  className={`inline-flex items-center px-3 h-full rounded-md text-sm font-medium whitespace-nowrap transition-all
                    ${exportFormat === k
                      ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">บัญชีต้นทาง ({exportFormat === 'ktb' ? 'KTB' : 'SCB'})</label>
            <select
              value={exportBankId}
              onChange={(e) => setExportBankId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600"
            >
              <option value="">— เลือกบัญชี {exportFormat === 'ktb' ? 'KTB' : 'SCB'} —</option>
              {formatBanks.map((b) => (
                <option key={b.id} value={b.id}>{b.name} · {b.account_number}</option>
              ))}
            </select>
            {formatBanks.length === 0 && <p className="text-[11px] text-red-500 mt-1">ไม่พบบัญชี {exportFormat === 'ktb' ? 'KTB' : 'SCB'} ที่เปิดใช้งาน</p>}
          </div>
          {selected.size === 0 ? (
            <div className="rounded-lg bg-gray-50 dark:bg-slate-700/50 p-4 text-center text-gray-400 dark:text-slate-500 text-sm">
              ยังไม่ได้ติ๊กเลือกรายการ — กลับไปติ๊กใบที่เข้าเงื่อนไขจากตารางก่อน
            </div>
          ) : (
            <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-x-auto">
              <table className="w-full min-w-[520px] text-xs">
                <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 text-left">เลขที่</th>
                    <th className="px-3 py-2 text-left">ผู้ถอน</th>
                    <th className="px-3 py-2 text-left">สถานะ</th>
                    <th className="px-3 py-2 text-right">ยอดเต็ม</th>
                    <th className="px-3 py-2 text-right">เติมแล้ว</th>
                    <th className="px-3 py-2 text-right">ส่วนค้าง (โอน)</th>
                    <th className="px-3 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {selectedList.map((p) => (
                    <tr key={p.id}>
                      <td className="px-3 py-2"><code className="text-[11px]">{p.payout_number}</code></td>
                      <td className="px-3 py-2 truncate max-w-[140px]">{p.merchant?.name || '-'}</td>
                      <td className="px-3 py-2"><StatusBadge status={p.status} statusMap={STATUS_MAP} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(p.amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-green-600 dark:text-green-400">{fmt(p.funded_amount)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-blue-600 dark:text-blue-400">{fmt(shortfallOf(p))}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => toggleSelect(p)} disabled={exporting}
                          className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50" title="เอาออกจากรายการ export">
                          <FontAwesomeIcon icon={faTimes} className="text-xs" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-slate-700/50 font-bold">
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-right">รวมส่วนค้าง</td>
                    <td className="px-3 py-2 text-right tabular-nums text-blue-600 dark:text-blue-400">{fmt(selectedList.reduce((s, p) => s + shortfallOf(p), 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </UiModal>

      {/* ประวัติการจับคู่ */}
      <UiModal open={!!historyOf} onClose={() => setHistoryOf(null)} title="ประวัติ order ที่จับคู่กับ payout นี้" icon={faClockRotateLeft} iconColor="text-indigo-500" size="lg" scrollable>
        {historyOf && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 mb-3 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-gray-500 dark:text-slate-400">Payout:</span> <strong className="text-gray-800 dark:text-slate-100">{historyOf.payout_number}</strong></div>
              <div><span className="text-gray-500 dark:text-slate-400">ยอด:</span> <strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmt(historyOf.amount)}</strong></div>
            </div>

            {/* แท็บ: ประวัติจับคู่ / Timeline */}
            <div className="inline-flex items-stretch h-9 gap-0.5 border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 rounded-lg p-0.5 mb-3">
              {([['pairs', 'ประวัติจับคู่', faClockRotateLeft], ['timeline', 'Timeline', faStream]] as const).map(([k, label, ic]) => (
                <button key={k} type="button" onClick={() => setHistTab(k)}
                  className={`inline-flex items-center gap-1.5 px-3 h-full rounded-md text-sm font-medium whitespace-nowrap transition-all
                    ${histTab === k
                      ? 'bg-white dark:bg-slate-600 text-gray-800 dark:text-slate-100 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'}`}>
                  <FontAwesomeIcon icon={ic} className="text-[11px]" />{label}
                  {k === 'timeline' && timeline.length > 0 && (
                    <span className="ml-0.5 text-[10px] px-1.5 rounded-full bg-gray-200 dark:bg-slate-500 text-gray-600 dark:text-slate-200">{timeline.length}</span>
                  )}
                </button>
              ))}
            </div>

            {histTab === 'timeline' ? (
              timelineLoading ? (
                <div className="text-center py-8 text-gray-400 dark:text-slate-500">กำลังโหลด...</div>
              ) : timeline.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-slate-500">ยังไม่มีเหตุการณ์</div>
              ) : (
                <div className="relative pl-1">
                  {/* เส้นแกน timeline */}
                  <div className="absolute left-[13px] top-2 bottom-2 w-px bg-gray-200 dark:bg-slate-600" />
                  <div className="space-y-3">
                    {timeline.map((ev) => {
                      const m = eventMeta(ev.event_type);
                      return (
                        <div key={ev.id} className="relative flex gap-3">
                          <div className="relative z-10 mt-0.5 flex-shrink-0">
                            <span className={`flex items-center justify-center w-[26px] h-[26px] rounded-full bg-white dark:bg-slate-800 ring-2 ring-gray-100 dark:ring-slate-700`}>
                              <FontAwesomeIcon icon={m.icon} className={`text-[11px] ${m.color}`} />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pb-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className={`text-sm font-medium ${m.color}`}>{m.label}</span>
                                {(() => {
                                  const ref = ev.deposit_order_id ? ev.order_number : ev.payout_number;
                                  return ref ? <code className="text-[10px] bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 px-1.5 py-0.5 rounded truncate">{ref}</code> : null;
                                })()}
                              </div>
                              {ev.amount != null && Number(ev.amount) !== 0 && (
                                <span className="text-sm font-bold tabular-nums text-gray-800 dark:text-slate-100 flex-shrink-0">{fmt(ev.amount)}</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 dark:text-slate-500">
                              {evTime(ev.created_at)}
                              {ev.actor && ev.actor !== 'system' ? ` · ${ev.actor}` : ' · ระบบ'}
                              {ev.from_status && ev.to_status ? ` · ${ev.from_status} → ${ev.to_status}` : ''}
                            </p>
                            {ev.reason && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 break-words">{ev.reason}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            ) : historyLoading ? (
              <div className="text-center py-8 text-gray-400 dark:text-slate-500">กำลังโหลด...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-gray-400 dark:text-slate-500">ยังไม่มีการจับคู่</div>
            ) : (
              <>
                {(() => {
                  const sums: Record<string, { count: number; amount: number }> = {};
                  history.forEach((h) => { const g = sums[h.status] || { count: 0, amount: 0 }; g.count++; g.amount += Number(h.amount || 0); sums[h.status] = g; });
                  return (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {HIST_GROUPS.filter((g) => sums[g.key]?.count).map((g) => (
                        <span key={g.key} className="text-xs px-2.5 py-1 rounded-lg bg-gray-50 dark:bg-slate-700/50">
                          <span className={`font-medium ${g.cls}`}>{g.label}</span>
                          <span className="text-gray-500 dark:text-slate-400"> {sums[g.key].count} · {fmt(sums[g.key].amount)}</span>
                        </span>
                      ))}
                    </div>
                  );
                })()}
                <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-x-auto">
                  <table className="w-full min-w-[640px] text-xs">
                    <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Order</th>
                        <th className="px-3 py-2 text-left font-medium">ผู้ฝาก</th>
                        <th className="px-3 py-2 text-left font-medium whitespace-nowrap">จับคู่</th>
                        <th className="px-3 py-2 text-left font-medium whitespace-nowrap">จ่าย</th>
                        <th className="px-3 py-2 text-left font-medium">อนุมัติ</th>
                        <th className="px-3 py-2 text-right font-medium">ยอด</th>
                        <th className="px-3 py-2 text-left font-medium">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                      {history.map((h) => {
                        const ap = h.status === 'paid' ? approvalInfo(h.approval_method) : null;
                        return (
                          <tr key={h.id}>
                            <td className="px-3 py-2 align-top">
                              <span className="text-sm font-medium text-gray-800 dark:text-slate-100">{h.order_number}</span>
                            </td>
                            <td className="px-3 py-2 align-top text-gray-600 dark:text-slate-300">
                              <span className="block truncate max-w-[140px]">{h.merchant?.name || '-'}</span>
                            </td>
                            <td className="px-3 py-2 align-top whitespace-nowrap text-gray-500 dark:text-slate-400">
                              {new Date(h.created_at).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-3 py-2 align-top whitespace-nowrap text-gray-500 dark:text-slate-400">
                              {h.paid_at ? new Date(h.paid_at).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : <span className="text-gray-300 dark:text-slate-600">—</span>}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {ap ? <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${ap.cls}`}>{ap.label}</span> : <span className="text-gray-300 dark:text-slate-600">—</span>}
                            </td>
                            <td className="px-3 py-2 align-top text-right whitespace-nowrap font-bold tabular-nums text-gray-800 dark:text-slate-100">{fmt(h.amount)}</td>
                            <td className="px-3 py-2 align-top"><StatusBadge status={h.status} statusMap={DEPOSIT_STATUS_MAP} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </UiModal>
    </>
  );
};

export default AdminP2pPayoutsPage;
